import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import PortfolioOverview from './components/PortfolioOverview';
import HoldingsTable from './components/HoldingsTable';
import PerformanceChart from './components/PerformanceChart';
import QuickStats from './components/QuickStats';
import ActionPanel from './components/ActionPanel';

const PortfolioTracker = () => {
  const [user] = useState({
    name: 'Alex Johnson',
    email: 'alex.johnson@example.com',
    subscriptionTier: 'pro'
  });

  // Mock portfolio data
  const portfolioData = {
    totalValue: 346150.50,
    dailyChange: 2.45,
    weeklyChange: -1.23,
    monthlyChange: 8.67
  };

  // Mock holdings data
  const [holdings] = useState([
    {
      id: 1,
      symbol: 'BTC',
      name: 'Bitcoin',
      quantity: 3.45672,
      currentPrice: 45250.00,
      value: 156420.50,
      costBasis: 142800.00,
      unrealizedPnL: 13620.50,
      unrealizedPnLPercent: 9.54,
      allocation: 45.2,
      priceChange24h: 2.1
    },
    {
      id: 2,
      symbol: 'ETH',
      name: 'Ethereum',
      quantity: 42.8934,
      currentPrice: 2316.75,
      value: 99340.20,
      costBasis: 95200.00,
      unrealizedPnL: 4140.20,
      unrealizedPnLPercent: 4.35,
      allocation: 28.7,
      priceChange24h: 1.8
    },
    {
      id: 3,
      symbol: 'ADA',
      name: 'Cardano',
      quantity: 85420.50,
      currentPrice: 0.49,
      value: 41890.30,
      costBasis: 44200.00,
      unrealizedPnL: -2309.70,
      unrealizedPnLPercent: -5.22,
      allocation: 12.1,
      priceChange24h: -0.8
    },
    {
      id: 4,
      symbol: 'SOL',
      name: 'Solana',
      quantity: 324.67,
      currentPrice: 90.72,
      value: 29450.80,
      costBasis: 31200.00,
      unrealizedPnL: -1749.20,
      unrealizedPnLPercent: -5.61,
      allocation: 8.5,
      priceChange24h: -2.3
    },
    {
      id: 5,
      symbol: 'DOT',
      name: 'Polkadot',
      quantity: 2840.25,
      currentPrice: 6.71,
      value: 19048.70,
      costBasis: 18500.00,
      unrealizedPnL: 548.70,
      unrealizedPnLPercent: 2.97,
      allocation: 5.5,
      priceChange24h: 0.5
    }
  ]);

  // Mock performance data
  const performanceData = [
    { date: '2024-11-01', portfolio: 298450, bitcoin: 285600 },
    { date: '2024-11-05', portfolio: 312800, bitcoin: 298200 },
    { date: '2024-11-10', portfolio: 295600, bitcoin: 289400 },
    { date: '2024-11-15', portfolio: 318900, bitcoin: 305800 },
    { date: '2024-11-20', portfolio: 334200, bitcoin: 318600 },
    { date: '2024-11-25', portfolio: 328700, bitcoin: 312400 },
    { date: '2024-11-30', portfolio: 341500, bitcoin: 325900 },
    { date: '2024-12-05', portfolio: 356800, bitcoin: 342100 },
    { date: '2024-12-10', portfolio: 349200, bitcoin: 335700 },
    { date: '2024-12-15', portfolio: 362400, bitcoin: 348900 },
    { date: '2024-12-20', portfolio: 358900, bitcoin: 345200 },
    { date: '2024-12-25', portfolio: 351600, bitcoin: 338800 },
    { date: '2024-12-30', portfolio: 346150, bitcoin: 332500 }
  ];

  // Mock stats data
  const statsData = {
    totalRealizedGains: 18170.50,
    totalUnrealizedGains: 14250.50,
    bestPerformer: { symbol: 'BTC', performance: 9.54 },
    worstPerformer: { symbol: 'SOL', performance: -5.61 },
    diversificationScore: 7.2,
    riskScore: 5.8
  };

  const handleLogin = () => {
    window.location.href = '/user-login';
  };

  const handleLogout = () => {
    window.location.href = '/user-login';
  };

  const handleUpgrade = () => {
    window.location.href = '/subscription-management';
  };

  const handleEditPosition = (position) => {
    console.log('Edit position:', position);
    // Implementation would open edit modal
  };

  const handleAddPosition = (positionData) => {
    console.log('Add position:', positionData);
    // Implementation would add new position to holdings
  };

  const handleExportReport = (format) => {
    console.log('Export report:', format);
    // Implementation would generate and download report
  };

  const handleRebalance = () => {
    console.log('Rebalance portfolio');
    // Implementation would show rebalancing suggestions
  };

  useEffect(() => {
    document.title = 'Portfolio Tracker - CryptoPredict Pro';
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={user}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onUpgrade={handleUpgrade}
      />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Portfolio Tracker</h1>
            <p className="text-muted-foreground">
              Monitor your cryptocurrency holdings and track performance analytics
            </p>
          </div>

          {/* Portfolio Overview */}
          <div className="mb-8">
            <PortfolioOverview 
              portfolioData={portfolioData}
              performanceData={performanceData}
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
            {/* Left Column - Holdings and Performance */}
            <div className="xl:col-span-3 space-y-8">
              <HoldingsTable 
                holdings={holdings}
                onEditPosition={handleEditPosition}
                onAddPosition={handleAddPosition}
              />
              
              <PerformanceChart 
                performanceData={performanceData}
                benchmarkData={performanceData}
              />
            </div>

            {/* Right Column - Stats and Actions */}
            <div className="xl:col-span-1 space-y-8">
              <QuickStats statsData={statsData} />
              
              <ActionPanel 
                onAddPosition={handleAddPosition}
                onExportReport={handleExportReport}
                onRebalance={handleRebalance}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PortfolioTracker;